import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@akuna/ui";
import { useAddFinanceiro } from "@/hooks/useHospitalization";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const TIPO_FIN = ["diaria", "medicamento", "exame", "procedimento", "outro"] as const;
type TipoFin = (typeof TIPO_FIN)[number];

const schema = z.object({
  descricao: z.string().optional(),
  tipo: z.enum(TIPO_FIN),
  quantidade: z.string().optional(),
  valor_unitario: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface FinanceiroItem {
  id: string;
  descricao: string;
  tipo: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
}

interface FinanceiroTabProps {
  hospitalizationId: string;
  customerId: string;
  itens: FinanceiroItem[];
}

export function FinanceiroTab({ hospitalizationId, customerId, itens }: FinanceiroTabProps) {
  const { t } = useTranslation("hospitalization");
  const [showForm, setShowForm] = useState(false);
  const addMutation = useAddFinanceiro();
  const total = itens.reduce((acc, i) => acc + (i.valor_total ?? 0), 0);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { descricao: "", tipo: "diaria", quantidade: "1", valor_unitario: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await addMutation.mutateAsync({
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
        descricao: values.descricao,
        tipo: values.tipo,
        quantidade: parseFloat(values.quantidade),
        valor_unitario: parseFloat(values.valor_unitario),
      });
      toast.success(t("success.financeiroSaved"));
      form.reset({ descricao: "", tipo: "diaria", quantidade: "1", valor_unitario: "" });
      setShowForm(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("financeiro.novoItem")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <FormField
                  control={form.control}
                  name="tipo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("financeiro.tipo")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIPO_FIN.map((tp) => (
                            <SelectItem key={tp} value={tp}>
                              {t(`financeiro.tipoOpcoes.${tp}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("financeiro.descricao")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="quantidade"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("financeiro.quantidade")}</FormLabel>
                        <FormControl>
                          <Input type="number" step="any" min="0" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="valor_unitario"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("financeiro.valorUnitario")}</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" min="0" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    {t("financeiro.cancelar")}
                  </Button>
                  <Button type="submit" disabled={addMutation.isPending}>
                    {t("financeiro.salvar")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {itens.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t("financeiro.semItens")}
        </p>
      )}

      {itens.map((item) => (
        <Card key={item.id}>
          <CardContent className="pt-3 pb-3 flex items-center gap-3">
            <Badge variant="outline">
              {t(`financeiro.tipoOpcoes.${item.tipo as TipoFin}`)}
            </Badge>
            <div className="flex-1">
              <p className="text-sm">{item.descricao}</p>
              <p className="text-xs text-muted-foreground">
                {item.quantidade} × R$ {item.valor_unitario.toFixed(2)}
              </p>
            </div>
            <span className="text-sm font-semibold">R$ {item.valor_total.toFixed(2)}</span>
          </CardContent>
        </Card>
      ))}

      {itens.length > 0 && (
        <>
          <Separator />
          <div className="flex justify-end">
            <p className="text-base font-semibold">
              {t("financeiro.totalGeral")}: R$ {total.toFixed(2)}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
